export interface Actor {
  id: string;
  workingName: string;
  inGameName?: string;
  inGameNameMode?: InGameNameMode;
  randomInGameNameType?: RandomInGameNameType;
  profilePicture?: number;
  positionId?: string;
  outfit?: string;
  preset?: string;
  gender: Gender;
}

export enum Gender {
  random = 0,
  male = 1,
  female = 2,
}

export enum InGameNameMode {
  manual = "manual",
  random = "random",
}

export enum RandomInGameNameType {
  neutral = "neutral",
  male = "male",
  female = "female",
}

export const GenderNeutralNames = ["Sam", "Remi", "Drew", "Rami", "Kai", "Andi", "Noa", "Kris", "Robin", "Lee"];

export function getDefaultRandomInGameNameType(
  gender: Gender | undefined
): RandomInGameNameType {
  if (gender === Gender.male) {
    return RandomInGameNameType.male;
  }

  if (gender === Gender.female) {
    return RandomInGameNameType.female;
  }

  return RandomInGameNameType.neutral;
}

export function normalizeActor(actor: Actor): Actor {
  const workingName = actor.workingName ?? "";
  const inGameNameMode =
    actor.inGameNameMode === InGameNameMode.random
      ? InGameNameMode.random
      : InGameNameMode.manual;
  const randomInGameNameType =
    actor.randomInGameNameType === RandomInGameNameType.male ||
    actor.randomInGameNameType === RandomInGameNameType.female
      ? actor.randomInGameNameType
      : actor.randomInGameNameType === RandomInGameNameType.neutral
        ? RandomInGameNameType.neutral
        : getDefaultRandomInGameNameType(actor.gender);

  return {
    ...actor,
    workingName,
    inGameNameMode,
    randomInGameNameType,
    inGameName:
      typeof actor.inGameName === "string" ? actor.inGameName : workingName,
  };
}

export function getUniqueActorWorkingName(
  workingName: string,
  actors: Actor[],
  excludeActorId?: string
): string {
  const trimmedName = workingName.trim();
  const baseName = trimmedName || "NPC";
  const usedNames = new Set(
    actors
      .filter((actor) => actor.id !== excludeActorId)
      .map((actor) => actor.workingName)
  );

  if (!usedNames.has(baseName)) {
    return baseName;
  }

  let suffix = 2;
  let candidate = `${baseName} ${suffix}`;

  while (usedNames.has(candidate)) {
    suffix += 1;
    candidate = `${baseName} ${suffix}`;
  }

  return candidate;
}

export function normalizeActorForCollection(
  actor: Actor,
  actors: Actor[]
): Actor {
  const normalizedActor = normalizeActor(actor);

  return {
    ...normalizedActor,
    workingName: getUniqueActorWorkingName(
      normalizedActor.workingName,
      actors,
      normalizedActor.id
    ),
  };
}

export function normalizeActors(actors: Actor[] | undefined): Actor[] {
  if (!Array.isArray(actors)) return [];

  return actors.reduce<Actor[]>((normalizedActors, actor) => {
    normalizedActors.push(normalizeActorForCollection(actor, normalizedActors));
    return normalizedActors;
  }, []);
}
